import { z } from "zod";
import { PhoneConfigSchema, AuthConfigSchema, VoiceAgentMappingsSchema } from "./agent";
import { CallsConfigurationSchema, CallDataSchema, AdvancementConditionSchema, AlertSchema } from "./phase";

// ============================================================================
// SNAPSHOT SCHEMAS
// These schemas match exactly what the PostgreSQL functions return:
// - agent_to_jsonb()
// - workflow_to_jsonb()
// - phases_to_jsonb_array()
// ============================================================================

// Agent snapshot (from agent_to_jsonb in 09_follow_ups.sql)
export const AgentSnapshotSchema = z.object({
  id: z.string(),
  agentGroupId: z.string(),
  releaseVersion: z.number().nullable().optional(),
  historyVersion: z.number().nullable().optional(),
  publishedSourceAgentId: z.string().nullable().optional(),
  workspaceId: z.string(),
  status: z.string().nullable().optional(),
  publicationStatus: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  voiceAgentsCreatedAt: z.string().nullable().optional(),
  releasedAt: z.string().nullable().optional(),
  archivedAt: z.string().nullable().optional(),
  deployedAt: z.string().nullable().optional(),
  activatedAt: z.string().nullable().optional(),
  releaseNote: z.string().nullable().optional(),
  publicationRequestedAt: z.string().nullable().optional(),
  publicationReviewedAt: z.string().nullable().optional(),
  publicationApprovedAt: z.string().nullable().optional(),
  publicationRejectedAt: z.string().nullable().optional(),
  publicationSuspendedAt: z.string().nullable().optional(),
  publicationReviewerId: z.string().nullable().optional(),
  publicationRejectionReason: z.string().nullable().optional(),
  publicationSuspensionReason: z.string().nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  phoneConfig: PhoneConfigSchema.nullable().optional().or(z.object({}).strict()),
  authConfig: AuthConfigSchema.nullable().optional().or(z.object({}).strict()),
  voiceAgentMappings: VoiceAgentMappingsSchema.nullable().optional().or(z.object({}).strict()),
  knowledgeBaseId: z.string().nullable().optional(),
  specialty: z.string().nullable().optional(),
  usageContext: z.string().nullable().optional(),
  operations: z.array(z.string()).nullable().optional(),
  patientTypes: z.array(z.string()).nullable().optional(),
  tags: z.array(z.string()).nullable().optional(),
  defaultLanguage: z.string().nullable().optional(),
  initialAuthorName: z.string().nullable().optional(),
  initialAuthorId: z.string().nullable().optional(),
  creatorId: z.string().nullable().optional(),
  creator: z
    .object({
      id: z.string(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      email: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});

// Workflow snapshot (from workflow_to_jsonb in 09_follow_ups.sql)
export const WorkflowSnapshotSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  releaseVersion: z.number().nullable().optional(),
  createdAt: z.string().nullable().optional(),
});

// Phase snapshot (from phases_to_jsonb_array in 09_follow_ups.sql)
export const PhaseSnapshotSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  phaseKey: z.string(),
  orderIndex: z.number(),
  knowledgeBaseId: z.string().nullable().optional(),
  callsConfiguration: CallsConfigurationSchema.nullable().optional().or(z.object({}).strict()),
  callDatas: z.array(CallDataSchema).nullable().optional().or(z.array(z.unknown())),
  advancementConditions: z.array(AdvancementConditionSchema).nullable().optional().or(z.array(z.unknown())),
  alerts: z.array(AlertSchema).nullable().optional().or(z.array(z.unknown())),
  createdAt: z.string().nullable().optional(),
});

export const PhasesSnapshotSchema = z.array(PhaseSnapshotSchema);

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type AgentSnapshot = z.infer<typeof AgentSnapshotSchema>;
export type WorkflowSnapshot = z.infer<typeof WorkflowSnapshotSchema>;
export type PhaseSnapshot = z.infer<typeof PhaseSnapshotSchema>;
export type PhasesSnapshot = z.infer<typeof PhasesSnapshotSchema>;
